// src/components/ui/selectCliente.jsx
import React from "react";
import AsyncSelect from "react-select/async";
import { components } from "react-select";
import { api } from "../../api";
import "../../sass/ui/selectCliente.scss";

const S = (v) => (v == null ? "" : String(v).trim());
const join = (arr, sep = " - ") => arr.filter(Boolean).join(sep);

const normalizarDocumentoFiscal = (valor) =>
  String(valor ?? "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");

const normalizarCpf = (valor) =>
  String(valor ?? "")
    .trim()
    .replace(/\D/g, "");

const calcularDigitoCnpjAlfa = (base, pesos) => {
  let soma = 0;

  for (let i = 0; i < pesos.length; i += 1) {
    const char = base[i];
    const valor = /^[0-9]$/.test(char)
      ? Number(char)
      : char.charCodeAt(0) - 48;

    soma += valor * pesos[i];
  }

  const resto = soma % 11;
  return resto < 2 ? 0 : 11 - resto;
};

const validarCpf = (valor) => {
  const cpf = normalizarCpf(valor);

  if (!/^\d{11}$/.test(cpf)) return false;
  if (/^(\d)\1{10}$/.test(cpf)) return false;

  let soma = 0;

  for (let i = 0; i < 9; i += 1) {
    soma += Number(cpf[i]) * (10 - i);
  }

  let digito1 = 11 - (soma % 11);
  if (digito1 >= 10) digito1 = 0;

  if (digito1 !== Number(cpf[9])) return false;

  soma = 0;

  for (let i = 0; i < 10; i += 1) {
    soma += Number(cpf[i]) * (11 - i);
  }

  let digito2 = 11 - (soma % 11);
  if (digito2 >= 10) digito2 = 0;

  return digito2 === Number(cpf[10]);
};

const validarCnpjAlfanumerico = (valor) => {
  const cnpj = normalizarDocumentoFiscal(valor);

  if (!/^[A-Z0-9]{14}$/.test(cnpj)) return false;
  if (/^([A-Z0-9])\1{13}$/.test(cnpj)) return false;

  const corpo = cnpj.slice(0, 12);
  const dv = cnpj.slice(12);

  if (!/^\d{2}$/.test(dv)) return false;

  const digito1 = calcularDigitoCnpjAlfa(corpo, [
    5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2,
  ]);

  const digito2 = calcularDigitoCnpjAlfa(corpo + String(digito1), [
    6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2,
  ]);

  return dv === `${digito1}${digito2}`;
};

const identificarDocumentoBusca = (valor) => {
  const termo = S(valor);
  const doc = normalizarDocumentoFiscal(termo);
  const cpf = normalizarCpf(termo);

  if (/^\d{11}$/.test(cpf) && validarCpf(cpf)) {
    return {
      tipo: "CPF",
      documento: cpf,
      valido: true,
    };
  }

  if (/^[A-Z0-9]{14}$/.test(doc)) {
    return {
      tipo: "CNPJ",
      documento: doc,
      valido: validarCnpjAlfanumerico(doc),
    };
  }

  return {
    tipo: null,
    documento: doc,
    valido: false,
  };
};

const truthy = (value) => {
  if (value === true || value === 1) return true;

  const text = String(value ?? "").trim().toLowerCase();

  return ["1", "true", "s", "sim", "yes"].includes(text);
};

const isNfseObrigatoria = (c) =>
  truthy(
    c?.NFSe_Obrigatoria ??
      c?.nfseObrigatoria ??
      c?.emissaoObrigatoria ??
      c?.clienteObrigatorio ??
      c?.obrigatorioNfse,
  );

const isInactiveCliente = (c) => {
  const tipCad = S(
    c?.TipCad ?? c?.tipCad ?? c?.TIPCAD ?? c?.tipoCadastro ?? c?.tipo_cadastro,
  ).toLowerCase();
  const des = S(
    c?.Desativado ?? c?.desativado ?? c?.DESATIVADO ?? c?.inativo ?? c?.Inativo,
  ).toLowerCase();

  return (
    tipCad === "cliente desistente" ||
    tipCad === "cliente_desistente" ||
    tipCad === "desistente" ||
    des === "true" ||
    des === "1" ||
    des === "s" ||
    des === "sim"
  );
};

const isBlockedCliente = (c) => {
  const blq = S(c?.BLQ ?? c?.blq).toUpperCase();
  return blq === "B";
};

const statusTag = (c) => {
  if (isInactiveCliente(c)) return "INATIVO";
  if (isBlockedCliente(c)) return "BLOQUEADO";
  return "ATIVO";
};

const normalizeCliente = (c) => {
  if (!c || typeof c !== "object") return c;

  const tokenLicencaNormalizado =
    c.tokenLicenca ??
    c.TokenLicenca ??
    c.token_licenca ??
    (c.licenca && (c.licenca.TOKEN_LICENCA || c.licenca.token_licenca)) ??
    (c.entrada &&
      c.entrada.licenca &&
      (c.entrada.licenca.TOKEN_LICENCA || c.entrada.licenca.token_licenca)) ??
    "";

  const documentoNormalizado =
    normalizarDocumentoFiscal(c.CpfCnpj || c.CpfCnpjLimpo) || "";

  return {
    ...c,
    tokenLicenca: tokenLicencaNormalizado,
    CpfCnpj: c.CpfCnpj ?? c.cpfCnpj ?? documentoNormalizado,
    CpfCnpjLimpo: c.CpfCnpjLimpo ?? c.cpfCnpjLimpo ?? documentoNormalizado,
    TipCad:
      c.TipCad ?? c.tipCad ?? c.TIPCAD ?? c.tipoCadastro ?? c.tipo_cadastro ?? "",
    Desativado:
      c.Desativado ?? c.desativado ?? c.DESATIVADO ?? c.inativo ?? c.Inativo ?? "",
    BLQ: c.BLQ ?? c.blq ?? "",
  };
};

const makeAddress = (c) => {
  if (S(c.enderecoLinha1) && S(c.enderecoLinha2)) {
    return `${c.enderecoLinha1} - ${c.enderecoLinha2}`;
  }

  const loco = S(c.Local || c.local);
  const log = S(c.Endereco);
  const num = S(c.Numero);
  const bair = S(c.Bairro);
  const cid = S(c.Cidade);
  const uf = S(c.UF);

  const logNum = join([log, num], ", ");
  const locLog = loco ? `${loco}: ${logNum}` : logNum;

  return join([locLog, bair, `${cid} / ${uf}`], " - ");
};

const makeLabel = (c, { showToken = false, showStatus = true } = {}) =>
  join(
    [
      S(c.CodigoCad),
      showStatus && `[${statusTag(c)}]`,
      isNfseObrigatoria(c) && "[NFS-e OBRIGATÓRIA]",
      S(c.RazaoSocial),
      S(c.NomeFant),
      S(c.CpfCnpj || c.CpfCnpjLimpo),
      S(c.TipCad),
      makeAddress(c),
      showToken && S(c.tokenLicenca) && `[${S(c.tokenLicenca)}]`,
    ],
    " - ",
  );

const toOptions = (data, labelOpts) => {
  const arr = Array.isArray(data) ? data : data ? [data] : [];

  return arr.map((c) => {
    const cli = normalizeCliente(c);

    return {
      value: cli,
      label: makeLabel(cli, labelOpts),
    };
  });
};

const reativarClienteApi = async (codigoCad) => {
  const id = Number(codigoCad);

  if (!Number.isInteger(id) || id <= 0) {
    throw new Error("CodigoCad inválido.");
  }

  const { data } = await api.patch(`/cliente/${id}/reativar`);
  return data?.cliente || null;
};

const carregarClientesFactory =
  ({ includeInactiveSearch, labelOpts } = {}) =>
  async (inputValue) => {
    const termo = S(inputValue);
    const termoAlfa = normalizarDocumentoFiscal(termo);
    const docInfo = identificarDocumentoBusca(termo);

    // Defesa de UX: mesmo que alguma rota legada devolva um desistente/inativo,
    // telas operacionais que não pediram inativos não devem exibi-lo.
    const mapOptions = (data) => {
      const options = toOptions(data, labelOpts);
      return includeInactiveSearch
        ? options
        : options.filter((opt) => !isInactiveCliente(opt?.value));
    };

    if (/^\d{1,6}$/.test(termo)) {
      try {
        const { data } = await api.get("/cliente/por-codigo", {
          params: {
            codigo: termo,
            incluirDesativados: includeInactiveSearch ? 1 : 0,
          },
        });

        return mapOptions(data);
      } catch (err) {
        console.error("Erro ao buscar cliente por código:", err);
        return [];
      }
    }

    if (termo.length < 3) return [];

    if (docInfo.tipo === "CPF" || docInfo.tipo === "CNPJ") {
      if (!docInfo.valido) {
        return [];
      }

      try {
        if (includeInactiveSearch) {
          const { data } = await api.get("/cliente/busca-geral", {
            params: {
              termo: docInfo.documento,
              limit: 20,
              incluirDesativados: 1,
            },
          });

          return mapOptions(data);
        }

        const { data } = await api.get("/cliente/por-cpfcnpj", {
          params: {
            documento: docInfo.documento,
          },
        });

        return mapOptions(data);
      } catch (err) {
        console.error("Erro ao buscar cliente por CPF/CNPJ:", err);
        return [];
      }
    }

    try {
      const { data } = await api.get("/cliente/busca-geral", {
        params: {
          termo: termoAlfa.length >= 14 ? termoAlfa : termo,
          limit: 20,
          incluirDesativados: includeInactiveSearch ? 1 : 0,
        },
      });

      return mapOptions(data);
    } catch (err) {
      console.error("Erro ao buscar cliente (busca geral):", err);
      return [];
    }
  };

const ClienteOption = (props) => {
  const c = props.data?.value || {};
  const status = statusTag(c);
  const obrigatoria = isNfseObrigatoria(c);

  return (
    <components.Option {...props}>
      <div className="cliente-option">
        <div className="cliente-option-top">
          <strong>{S(c.RazaoSocial) || S(c.NomeFant) || "Cliente"}</strong>

          <div className="cliente-option-tags">
            <span className="cliente-tag codigo">
              #{S(c.CodigoCad) || "—"}
            </span>

            <span className={`cliente-tag status ${status.toLowerCase()}`}>
              {status}
            </span>

            {obrigatoria && (
              <span className="cliente-tag nfse">NFS-e obrigatória</span>
            )}
          </div>
        </div>

        <div className="cliente-option-sub">
          <span>{S(c.NomeFant) || "Sem fantasia"}</span>
          <span>{S(c.CpfCnpj || c.CpfCnpjLimpo) || "Sem documento"}</span>
          <span>{S(c.TipCad) || "Sem tipo"}</span>
        </div>

        <div className="cliente-option-address">
          {makeAddress(c) || "Endereço não informado"}
        </div>
      </div>
    </components.Option>
  );
};

const SingleValue = (props) => {
  const c = props.data?.value || {};

  return (
    <components.SingleValue {...props}>
      <span className="cliente-single">
        <strong>{S(c.CodigoCad) || "—"}</strong>
        <span>{S(c.RazaoSocial) || S(c.NomeFant) || "Cliente"}</span>
        {isNfseObrigatoria(c) && <em>NFS-e</em>}
      </span>
    </components.SingleValue>
  );
};

const SelectCliente = ({
  onChange,
  value = null,
  className = "select-cliente",
  includeInactiveSearch = false,
  autoReactivateOnSelect = false,
  confirmReactivate = true,
  showTokenOnLabel = false,
  isDisabled = false,
  placeholder = "Busque por razão, fantasia, código, CPF/CNPJ, telefone, email, cidade, bairro, endereço...",
}) => {
  const labelOpts = React.useMemo(
    () => ({
      showToken: !!showTokenOnLabel,
      showStatus: true,
    }),
    [showTokenOnLabel],
  );

  const loadOptions = React.useMemo(
    () => carregarClientesFactory({ includeInactiveSearch, labelOpts }),
    [includeInactiveSearch, labelOpts],
  );

  const normalizedValue = value ? normalizeCliente(value) : null;

  const currentOption = React.useMemo(() => {
    if (!normalizedValue) return null;

    return {
      value: normalizedValue,
      label: makeLabel(normalizedValue, labelOpts),
    };
  }, [normalizedValue, labelOpts]);

  const handleChange = async (opt) => {
    const selected = opt?.value ? normalizeCliente(opt.value) : null;

    if (!selected) {
      return onChange(null);
    }

    if (autoReactivateOnSelect && isInactiveCliente(selected)) {
      const ok =
        !confirmReactivate ||
        window.confirm(
          `Cliente ${S(selected.CodigoCad)} está INATIVO. Deseja reativar agora?`,
        );

      if (!ok) {
        return onChange(selected);
      }

      try {
        const clienteReativado = await reativarClienteApi(selected.CodigoCad);

        const merged = normalizeCliente({
          ...selected,
          ...(clienteReativado || {}),
        });

        return onChange(merged);
      } catch (e) {
        console.error("Falha ao reativar cliente:", e);
        return onChange(selected);
      }
    }

    return onChange(selected);
  };

  return (
    <AsyncSelect
      className={className}
      classNamePrefix="react-select"
      cacheOptions
      defaultOptions={false}
      loadOptions={loadOptions}
      onChange={handleChange}
      placeholder={placeholder}
      isClearable
      isDisabled={isDisabled}
      value={currentOption}
      components={{
        Option: ClienteOption,
        SingleValue,
      }}
      getOptionValue={(opt) =>
        String(
          opt?.value?.ID ||
            `${S(opt?.value?.CodigoCad)}-${normalizarDocumentoFiscal(
              opt?.value?.CpfCnpj || opt?.value?.CpfCnpjLimpo,
            )}`,
        )
      }
      noOptionsMessage={({ inputValue }) => {
        const t = S(inputValue);
        const docInfo = identificarDocumentoBusca(t);

        if (/^\d{1,6}$/.test(t)) {
          return "Nenhum cliente encontrado para esse código";
        }

        if (docInfo.tipo === "CPF" && !docInfo.valido) {
          return "CPF inválido";
        }

        if (docInfo.tipo === "CNPJ" && !docInfo.valido) {
          return "CNPJ inválido";
        }

        if (docInfo.tipo === "CPF" || docInfo.tipo === "CNPJ") {
          return "Nenhum cliente encontrado para esse CPF/CNPJ";
        }

        if (t.length < 3) {
          return "Digite pelo menos 3 caracteres";
        }

        return "Nenhum cliente encontrado";
      }}
      loadingMessage={() => "Consultando clientes..."}
    />
  );
};

export default SelectCliente;